import type { ReactNode } from 'react';

import { isApiError } from '@/lib/api';

import { Button } from './Button';
import styles from './StateMessage.module.css';

export interface ErrorStateProps {
  /** The thrown value. `ApiError` supplies the pt-BR copy and the correlation id. */
  error?: unknown;
  title?: string | undefined;
  description?: string | undefined;
  onRetry?: (() => void) | undefined;
  retryLabel?: string | undefined;
  action?: ReactNode | undefined;
  className?: string | undefined;
}

const DEFAULT_TITLE = 'Não foi possível carregar';
const DEFAULT_DESCRIPTION = 'Ocorreu um erro inesperado. Tente novamente.';

/** Shared failure treatment: every feature screen renders this instead of its own error copy. */
export function ErrorState({
  error,
  title,
  description,
  onRetry,
  retryLabel = 'Tentar novamente',
  action,
  className,
}: ErrorStateProps) {
  const apiError = isApiError(error) ? error : null;
  // `apiError.title` is deliberately not read here: it is the backend's own English string
  // ("Forbidden", "Internal Server Error", ...), and `userTitle` is `ApiError`'s pt-BR headline
  // for the same status — see its doc comment in `lib/api/errors.ts` for why `title` is kept on
  // the object at all instead of being dropped.
  const resolvedTitle = title ?? apiError?.userTitle ?? DEFAULT_TITLE;
  const resolvedDescription = description ?? apiError?.userMessage ?? DEFAULT_DESCRIPTION;

  return (
    <div className={[styles.state, className].filter(Boolean).join(' ')} role="alert">
      <span className={[styles.icon, styles.iconDanger].join(' ')} aria-hidden="true">
        !
      </span>
      <p className={styles.title}>{resolvedTitle}</p>
      <p className={styles.description}>{resolvedDescription}</p>

      {onRetry !== undefined || action !== undefined ? (
        <div className={styles.actions}>
          {onRetry !== undefined ? (
            <Button variant="secondary" onClick={onRetry}>
              {retryLabel}
            </Button>
          ) : null}
          {action}
        </div>
      ) : null}

      {apiError?.requestId != null ? (
        <p className={styles.details}>
          Código de referência: <span className={styles.requestId}>{apiError.requestId}</span>
        </p>
      ) : null}
    </div>
  );
}
